import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import { Button, Col, Divider, Form, Input, InputRef, notification, Select } from "antd";
import { PlusOutlined } from "@ant-design/icons";

import { ETipoAtributoConfiguracaoContabil, IAtributoConfiguracaoContabil } from "../../../../../../interfaces";

import { rowColParametrosConfiguracaoContabil } from "../GridStyle";
import { mask, unMask } from "../../../../../../../../../utils/MascaraDeCampos";
import cnpjValidator from "../../../../../../../../../utils/Validators/cnpjValidator";
import { onlyNumbers } from "../../../../../../../utils";
import RadioInput from "../../../../../../../components/RadioInput";

export interface IInputConfiguracaoContabilCustomChangeParams {
    field: string,
    value: string | number | number[] | string[],
    validate?: boolean
}

interface IProps {
    atributo: IAtributoConfiguracaoContabil;
    onCustomChange: (params: IInputConfiguracaoContabilCustomChangeParams) => void;
    initialValues: { [key: string]: any } | undefined;
}

export default function InputConfiguracaoContabil({
    atributo,
    onCustomChange,
    initialValues,
}: IProps) {
    const [arrayOptions, setArrayOptions] = useState<number[] | string[]>([]);
    const [arrayOptionToAdd, setArrayOptionToAdd] = useState('');

    const arrayInputRef = useRef<InputRef>(null);

    const isCnpj = atributo.nomeNorm.toLowerCase().includes('cnpj');
    const isNumber = atributo.tipo === ETipoAtributoConfiguracaoContabil.NUMBER;

    const handleChangeCnpj = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        const originalValue = unMask(e.target.value);
        const maskedValue = mask(originalValue, [
            "99.999.999/9999-99"
        ]);

        onCustomChange({
            field: atributo.nomeNorm,
            value: maskedValue,
            validate: maskedValue.length === 18
        });
    }, [atributo]);

    const handleChangeNumber = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        const value = onlyNumbers(e.target.value);

        onCustomChange({
            field: atributo.nomeNorm,
            value: Number(value),
        });
    }, [atributo]);

    const handleAddArrayItem = useCallback((e) => {
        setArrayOptions((prevState) => {
            const value = (atributo.tipoArray === 'number' ? Number(arrayOptionToAdd) : arrayOptionToAdd);

            if ((prevState as any[]).find((e) => e === value)) {
                notification.error({ message: "Esse item já existe na lista" });

                return prevState;
            }

            const data = [...prevState, value as any];

            onCustomChange({
                field: atributo.nomeNorm,
                value: data,
            });

            return data;
        });

        setArrayOptionToAdd('');
        arrayInputRef.current?.focus();
    }, [arrayOptionToAdd, atributo]);

    const handleChangeArray = useCallback((value: string[] | number[]) => {
        setArrayOptions(value);
    }, []);

    const handleChangeInputArray = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        const valor = atributo.tipoArray === 'number' ? onlyNumbers(e.target.value) : e.target.value;

        setArrayOptionToAdd(valor);
    }, [atributo]);

    useEffect(() => {
        if (atributo.tipo !== ETipoAtributoConfiguracaoContabil.ARRAY || !initialValues) {
            return;
        }

        setArrayOptions(initialValues[atributo.nomeNorm] || []);
    }, [atributo, initialValues]);

    return (
        <Col {...rowColParametrosConfiguracaoContabil}>
            <Form.Item
                name={atributo.nomeNorm}
                label={atributo.nome}
                rules={isCnpj ? [
                    {
                        max: 18,
                        min: 18,
                        message: 'CNPJ inválido',
                        // array vazio para validar apenas no momento do submit
                        validateTrigger: [],
                    },
                    {
                        required: true,
                        message: 'Campo obrigatório',
                    },
                    () => ({
                        validator(rule, value) {
                            return new Promise((resolve: (value?: any) => void, reject) => {
                                if (value != '' && value != null) {
                                    value = unMask(value);
                                    if (value.length === 14) {
                                        const isCnpjValid = cnpjValidator.validate(value);

                                        if (isCnpjValid) {
                                            resolve();
                                        } else {
                                            reject('CNPJ inválido');
                                        }
                                    } else {
                                        resolve();
                                    }
                                } else {
                                    resolve('');
                                }
                            });
                        },
                    }),
                ] : [{
                    required: true,
                    message: 'Campo obrigatório',
                }]}
            >
                {
                    [ETipoAtributoConfiguracaoContabil.BOOLEAN].includes(atributo.tipo) ? (
                        <RadioInput
                            options={[
                                {
                                    value: true,
                                    label: "Sim",
                                },
                                {
                                    value: false,
                                    label: "Não",
                                },
                            ]}
                        />
                    ) : [ETipoAtributoConfiguracaoContabil.ENUM].includes(atributo.tipo) ? (
                        <Select
                            options={atributo.options?.map((e) => ({ value: e, label: e })) || []}
                            placeholder={atributo.nome}
                        />
                    ) : [ETipoAtributoConfiguracaoContabil.ARRAY].includes(atributo.tipo) ? (
                        <Select
                            dropdownRender={menu => (
                                <>
                                    {menu}
                                    <Divider style={{ margin: '8px 0' }} />
                                    <div
                                        style={{
                                            display: 'flex',
                                            padding: '0 8px 4px',
                                            gap: 8,
                                        }}
                                    >
                                        <Input
                                            placeholder="Insira um item"
                                            ref={arrayInputRef}
                                            value={arrayOptionToAdd}
                                            onChange={handleChangeInputArray}
                                            onKeyDown={(e) => {
                                                e.stopPropagation();
                                            }}
                                        />

                                        <Button
                                            type="text"
                                            icon={<PlusOutlined />}
                                            onClick={handleAddArrayItem}
                                        >
                                            Adicionar
                                        </Button>

                                    </div>
                                </>
                            )}
                            options={arrayOptions.map(item => ({ label: item, value: item }))}
                            mode="multiple"
                            onChange={handleChangeArray}
                        />
                    ) : (
                        <Input
                            placeholder={atributo.nome}
                            autoComplete="off"
                            onChange={isCnpj ? handleChangeCnpj : isNumber ? handleChangeNumber : undefined}
                        />
                    )
                }
            </Form.Item>
        </Col>
    )
}
